import { lang } from './i18n'

const LOCALE = lang === 'th' ? 'th-TH' : 'en-US'

export const formatMoney = (amount: number, currency = 'THB'): string => {
  if (currency === 'THB') {
    return `฿${amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

export const formatDate = (date: string | null | undefined): string => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const formatDatetime = (date: string | null | undefined): string => {
  if (!date) return '—'
  return new Date(date).toLocaleString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const formatRelativeTime = (date: string | null | undefined): string => {
  if (!date) return '—'
  const now = new Date()
  const d = new Date(date)
  const diffMs = now.getTime() - d.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  const th = lang === 'th'
  if (diffSecs < 60) return th ? 'เมื่อสักครู่' : 'just now'
  if (diffMins < 60) return th ? `${diffMins} นาทีที่แล้ว` : `${diffMins}m ago`
  if (diffHours < 24) return th ? `${diffHours} ชม.ที่แล้ว` : `${diffHours}h ago`
  if (diffDays < 7) return th ? `${diffDays} วันที่แล้ว` : `${diffDays}d ago`
  return formatDate(date)
}

export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export const cn = (...classes: (string | undefined | false | null)[]): string =>
  classes.filter(Boolean).join(' ')

export const getPlanColor = (plan: string): string => {
  const p = plan.toLowerCase()
  if (p.includes('enterprise')) return 'bg-amber-100 text-amber-800 border-amber-200'
  if (p.includes('business')) return 'bg-purple-100 text-purple-800 border-purple-200'
  if (p.includes('pro')) return 'bg-indigo-100 text-indigo-800 border-indigo-200'
  return 'bg-slate-100 text-slate-700 border-slate-200' // starter / default
}

export const getPlanDotColor = (plan: string): string => {
  const p = plan.toLowerCase()
  if (p.includes('enterprise')) return 'bg-amber-500'
  if (p.includes('business')) return 'bg-purple-500'
  if (p.includes('pro')) return 'bg-indigo-500'
  return 'bg-slate-400'
}

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'active':
    case 'resolved':
    case 'healthy':
    case 'paid':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'trial':
    case 'open':
    case 'info':
    case 'pending':
    case 'sending':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'past_due':
    case 'degraded':
    case 'warning':
      return 'bg-amber-100 text-amber-800 border-amber-200'
    case 'suspended':
    case 'failed':
    case 'urgent':
    case 'down':
      return 'bg-red-100 text-red-800 border-red-200'
    case 'expired':
    case 'cancelled':
    case 'closed':
    case 'gray':
      return 'bg-gray-100 text-gray-700 border-gray-200'
    case 'locked_upgrade':
      return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'disabled_maintenance':
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case 'hidden':
      return 'bg-gray-100 text-gray-600 border-gray-200'
    case 'visible':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'sent':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'refunded':
      return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'low':
      return 'bg-gray-100 text-gray-600 border-gray-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

export const truncate = (str: string, maxLen = 40): string => {
  if (str.length <= maxLen) return str
  return str.slice(0, maxLen) + '...'
}

export const capitalize = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ')

export const moneyLogTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    subscription_payment: 'Subscription',
    refund: 'Refund',
    credit_adjustment: 'Credit Adj.',
    debit_adjustment: 'Debit Adj.',
    failed_payment: 'Failed Payment',
    manual_charge: 'Manual Charge',
    wallet_credit: 'Wallet Credit',
    wallet_debit: 'Wallet Debit',
  }
  return labels[type] || capitalize(type)
}
